import { useCallback, useMemo, useState } from 'react';
import { SingleValue } from 'react-select';
import clsx from 'clsx';

import { Chip } from 'components/Chip/Chip';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { GraniteSelect, OptionType } from 'components/Select/Select';
import { ServerPaginatedTable } from 'components/Table/ServerPaginatedTable';
import { useCustomizeTHead } from 'hooks/useCustomizeTHead';
import { usePaginatedTable } from 'hooks/usePaginatedTable';
import { useQueryParams } from 'hooks/useQueryParams';
import { useQueryState } from 'hooks/useQueryState';
import { useSortedTable } from 'hooks/useSortedTable';
import { EmptySearchResults } from 'screens/QuoteIndex/QuoteIndexPage';
import { setCookie, toSentenceCase } from 'shared/util/util';
import { getSupportRequests } from '../utils';
import { defaultPreferences, tableHeaders } from './constants';
import { EmptySupportRequests } from './EmptySupportRequests';
import { SupportRequestsFilterSearchParams } from '../schema';

const SERVICES_STALE_INTERVAL = 60_000; // 1 minute

const RequestsTable = () => {
  const { resetQueryParams } = useQueryParams({});

  const [pageSize, setPageSize] = useState<number>();

  const [statusFilter, setStatusFilter] = useQueryState('status', {
    defaultValue: null as SingleValue<OptionType>,
    getParam: (value) => ({ label: value, value: value }),
    setParam: (value) => (value ? value.value : ''),
  });
  const [typeFilter, setTypeFilter] = useQueryState('type', {
    defaultValue: null as SingleValue<OptionType>,
    getParam: (value) => ({ label: value, value: value }),
    setParam: (value) => (value ? value.value : ''),
  });
  const [openedByFilter, setOpenedByFilter] = useQueryState('opened_by', {
    defaultValue: null as SingleValue<OptionType>,
    getParam: (value) => ({ label: value, value: value }),
    setParam: (value) => (value ? value.value : ''),
  });
  const [openedFilter, setOpenedFilter] = useQueryState('opened', {
    defaultValue: null as SingleValue<OptionType>,
    getParam: (value) => ({ label: value, value: value }),
    setParam: (value) => (value ? value.value : ''),
  });

  const { sortingQueryParams, sortingState, onSortingChange } = useSortedTable({
    initialSorting: [{ id: 'opened', desc: true }],
  });

  const sortingParams = useMemo(
    () => ({
      sort_by: sortingState?.[0]?.id ?? 'opened',
      sort_order: sortingState?.[0]?.desc ? 'desc' : 'asc',
    }),
    [sortingState],
  );

  const paginationChanged = (page: number) => {
    setPageSize(page);
    setCookie('paginationSizeSupportRequests', page.toString(), 365);
  };

  const buildSupportRequestsQueryParams = useCallback(
    (args?: SupportRequestsFilterSearchParams) => ({
      ...sortingQueryParams,
      ...sortingParams,
      ...(statusFilter ? { status: statusFilter?.value } : {}),
      ...(typeFilter ? { type: typeFilter?.value } : {}),
      ...(openedByFilter ? { opened_by: openedByFilter?.value } : {}),
      ...(openedFilter ? { date_range: openedFilter?.value } : {}),
      ...args,
    }),
    [
      sortingQueryParams,
      sortingParams,
      statusFilter,
      typeFilter,
      openedByFilter,
      openedFilter,
    ],
  );

  const getSupportRequestsIndexFn = useCallback(
    (args?: SupportRequestsFilterSearchParams) => {
      return getSupportRequests(buildSupportRequestsQueryParams(args));
    },
    [buildSupportRequestsQueryParams],
  );

  const {
    data: tablePageData,
    metadata,
    ...paginatedTableProps
  } = usePaginatedTable(
    getSupportRequestsIndexFn,
    {
      statusFilter,
      typeFilter,
      openedByFilter,
      openedFilter,
      sortingQueryParams,
    },
    [
      'support-request-index-table',
      statusFilter,
      typeFilter,
      openedByFilter,
      openedFilter,
      sortingQueryParams,
    ],
    {
      refetchOnMount: true,
      staleTime: SERVICES_STALE_INTERVAL,
    },
  );

  const clearAllFilters = useCallback(() => {
    setOpenedFilter(null);
    setStatusFilter(null);
    setOpenedByFilter(null);
    setTypeFilter(null);
    resetQueryParams();
  }, [
    setOpenedFilter,
    setStatusFilter,
    resetQueryParams,
    setOpenedByFilter,
    setTypeFilter,
  ]);

  const preferenceFilterClearFunctions: Record<
    string,
    (() => void) | undefined
  > = {
    status: statusFilter ? () => setStatusFilter(null) : undefined,
    ticket_type: typeFilter ? () => setTypeFilter(null) : undefined,
    opened_by: openedByFilter ? () => setOpenedByFilter(null) : undefined,
    opened: openedFilter ? () => setOpenedFilter(null) : undefined,
  };

  const {
    columns,
    hasFilterByPreference,
    isPreferenceLoading,
    customizeFirstColumnMobileClass,
  } = useCustomizeTHead({
    mockPreferencesFn: () => defaultPreferences,
    customizeFirstColumnMobileKey: 'account_number',
    columns: tableHeaders,
    tableName: 'support-request-view',
    onPreferenceRemoved: (field) => {
      preferenceFilterClearFunctions[field]?.();
    },
  });

  const enhancedMetadata = metadata as {
    statuses?: string[];
    types?: string[];
    opened_bys?: string[];
    opened?: string[];
  };

  const filterOptions = {
    statuses: enhancedMetadata?.statuses ?? [],
    types: enhancedMetadata?.types ?? [],
    opened_by: enhancedMetadata?.opened_bys ?? [],
    opened: ['Past Month', 'Past 3 Months', 'Past Year'],
  };

  return (
    <div className="flex w-full flex-col items-start justify-start">
      <div className="mb-4 flex w-full flex-wrap items-start justify-between gap-4">
        <div className="grid w-full grid-cols-4 gap-4 md:flex">
          <GraniteSelect
            options={filterOptions.statuses.map((item: string) => ({
              label: toSentenceCase(item),
              value: item,
            }))}
            className={clsx(
              'col-span-2',
              !hasFilterByPreference('status') && '!hidden',
            )}
            placeholder="Filter ticket status"
            controlShouldRenderValue={false}
            isSearchable={false}
            onChange={setStatusFilter}
            value={statusFilter}
          />
          <GraniteSelect
            options={filterOptions.types.map((item: string) => ({
              label: item,
              value: item,
            }))}
            className={clsx(
              'col-span-2',
              !hasFilterByPreference('ticket_type') && '!hidden',
            )}
            placeholder="Filter ticket type"
            controlShouldRenderValue={false}
            isSearchable={false}
            onChange={setTypeFilter}
            value={typeFilter}
          />
          <GraniteSelect
            options={filterOptions.opened_by.map((item: string) => ({
              label: item,
              value: item,
            }))}
            className={clsx(
              'col-span-2',
              !hasFilterByPreference('opened_by') && '!hidden',
            )}
            placeholder="Filter by submitter"
            isClearable={false}
            isSearchable={false}
            controlShouldRenderValue={false}
            onChange={setOpenedByFilter}
            value={openedByFilter}
          />
          <GraniteSelect
            options={filterOptions.opened.map((item: string) => ({
              label: toSentenceCase(item),
              value: item,
            }))}
            className={clsx(
              'col-span-2',
              !hasFilterByPreference('opened') && '!hidden',
            )}
            placeholder="Filter ticket by date"
            isClearable={false}
            isSearchable={false}
            controlShouldRenderValue={false}
            onChange={setOpenedFilter}
            value={openedFilter}
          />
          <GraniteButton
            variant="secondary"
            size="large"
            onClick={clearAllFilters}
            className={clsx(
              '!box-border whitespace-nowrap md:max-w-[175px]',
              'col-span-full w-full md:col-span-1',
            )}
          >
            Clear all filters
          </GraniteButton>
        </div>
      </div>
      <div className="mb-12 flex flex-wrap gap-4">
        {statusFilter?.value && (
          <Chip
            label={statusFilter.label}
            onDelete={() => {
              setStatusFilter(null);
            }}
          />
        )}
        {typeFilter?.value && (
          <Chip
            label={typeFilter.label}
            onDelete={() => {
              setTypeFilter(null);
            }}
          />
        )}
        {openedByFilter?.value && (
          <Chip
            label={openedByFilter.label}
            onDelete={() => {
              setOpenedByFilter(null);
            }}
          />
        )}
        {openedFilter?.value && (
          <Chip
            label={openedFilter.label}
            onDelete={() => {
              setOpenedFilter(null);
            }}
          />
        )}
      </div>
      <ServerPaginatedTable
        data={tablePageData ?? []}
        columns={columns}
        title="My services"
        sortingState={sortingState}
        onSortingChange={onSortingChange}
        paginationChanged={paginationChanged}
        paginationSizeStored={pageSize}
        emptyDataElement={
          statusFilter?.value ||
          openedFilter?.value ||
          typeFilter?.value ||
          openedByFilter?.value ? (
            <EmptySearchResults />
          ) : (
            <EmptySupportRequests />
          )
        }
        {...paginatedTableProps}
        isFetchingData={
          paginatedTableProps.isFetchingData || isPreferenceLoading
        }
        tableContainerClassName={customizeFirstColumnMobileClass}
      />
    </div>
  );
};

export default RequestsTable;
